import { useTranslation } from "react-i18next";
import { Text } from "react-native-paper";
import { MAX_ITEM_TYPE_ROWS } from "../../constants";
import type { IconType } from "../../icon";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import type { ConnectedLevel } from "../ConnectedLevels";
import {
	type ItemTypeProgressRow,
	ItemTypesCard,
} from "../SkillProgressCards/ItemTypesCard";
import {
	type ProficiencyCardContent,
	ProficiencyLevelsCard,
	type ProficiencyScoreDonut,
} from "../SkillProgressCards/ProficiencyLevelsCard";
import type { ProficiencyScoresEmptyCopy } from "../SkillProgressCards/ProficiencyScoresEmptyState";

/**
 * Data-only mirror of `ProficiencyCardContent`: the empty variant carries copy
 * but no handler, so a data hook can build a section without owning navigation.
 */
export type LanguageLearningProficiencyContent =
	| { kind: "levels"; levels: ConnectedLevel[]; tooltip?: string }
	| { kind: "scores"; scores: ProficiencyScoreDonut[] }
	| { kind: "noScores"; empty: ProficiencyScoresEmptyCopy };

export type LanguageLearningSkillSection = {
	id: string;
	title: string;
	icon: IconType;
	subtitle?: string;
	proficiency: LanguageLearningProficiencyContent;
	proficiencyCardSubtitle?: string;
	itemTypeRows: ItemTypeProgressRow[];
	proficiencyTip?: string;
	itemTypesTip?: string;
	tipLoading?: boolean;
	hideItemTypes?: boolean;
};

type Props = {
	section: LanguageLearningSkillSection;
	onViewAllItemTypes: (sectionId: string) => void;
	onViewItemTypeDetails?: (questionTypeId: string) => void;
	onStartTest?: (sectionId: string, sectionTitle: string) => void;
};

export const SkillSection = ({
	section,
	onViewAllItemTypes,
	onViewItemTypeDetails,
	onStartTest,
}: Props) => {
	const { isMobile } = useDevice();
	const { t } = useTranslation();

	const hasMoreRows = section.itemTypeRows.length > MAX_ITEM_TYPE_ROWS;
	const visibleRows = section.itemTypeRows.slice(0, MAX_ITEM_TYPE_ROWS);

	const proficiencyContent: ProficiencyCardContent =
		section.proficiency.kind === "noScores"
			? {
					kind: "noScores",
					empty: {
						...section.proficiency.empty,
						onPressCta: onStartTest
							? () => onStartTest(section.id, section.title)
							: undefined,
					},
				}
			: section.proficiency;

	const proficiencyCard = (
		<ProficiencyLevelsCard
			title={t("progress.languageLearning.proficiencyCardTitle", {
				skill: section.title,
			})}
			subtitle={section.proficiencyCardSubtitle ?? section.subtitle}
			icon={section.icon}
			content={proficiencyContent}
			fillHeight={!isMobile}
			tip={section.proficiencyTip}
			tipLoading={section.tipLoading}
		/>
	);
	const itemTypesCard = section.hideItemTypes ? null : (
		<ItemTypesCard
			title={t("progress.languageLearning.itemTypesCardTitle", {
				skill: section.title,
			})}
			subtitle={t("progress.languageLearning.itemTypesCardSubtitle", {
				count: section.itemTypeRows.length,
			})}
			icon={section.icon}
			rows={visibleRows}
			fillHeight={!isMobile}
			tip={section.itemTypesTip}
			tipLoading={section.tipLoading}
			onViewAllPress={
				hasMoreRows ? () => onViewAllItemTypes(section.id) : undefined
			}
			onViewDetails={onViewItemTypeDetails}
		/>
	);

	return (
		<Box gap={isMobile ? 16 : 8}>
			<Box paddingHorizontal={isMobile ? 16 : 0}>
				<Text variant="titleMedium">{section.title}</Text>
			</Box>
			{isMobile || !itemTypesCard ? (
				<Box gap={16}>
					{proficiencyCard}
					{itemTypesCard}
				</Box>
			) : (
				<Box flexDirection="row" alignItems="stretch" gap={16}>
					<Box flex={1}>{proficiencyCard}</Box>
					<Box flex={1}>{itemTypesCard}</Box>
				</Box>
			)}
		</Box>
	);
};
